import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Percent,
  TrendingUp,
  BarChart3
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import sacMasterService from '../../services/sacMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const SACMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalSACs: 0,
    activeSACs: 0,
    inactiveSACs: 0,
    gstRateStats: []
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedSACData, setSelectedSACData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [sacsResponse, statsResponse] = await Promise.all([
        sacMasterService.getAllSACs(),
        sacMasterService.getSACStats()
      ])
      
      if (sacsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (sacsResponse.data || []).map(sac => ({
          ...sac,
          id: sac._id,
          formattedGstRate: sac.gstRate ? `${sac.gstRate.toFixed(2)}%` : '0.00%',
          formattedIgstRate: sac.igstRate ? `${sac.igstRate.toFixed(2)}%` : '0.00%',
          formattedSgstRate: sac.sgstRate ? `${sac.sgstRate.toFixed(2)}%` : '0.00%',
          formattedCgstRate: sac.cgstRate ? `${sac.cgstRate.toFixed(2)}%` : '0.00%',
          formattedUtgstRate: sac.utgstRate ? `${sac.utgstRate.toFixed(2)}%` : '0.00%',
          currentVersion: `Rev ${sac.currentRevision || 1}`
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (sacsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No SAC master records found. Create your first SAC!')
        }
      } else {
        toast.error(sacsResponse.message || 'Failed to load SAC master records')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading SAC master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view SAC master records')
      } else {
        toast.error('Failed to load SAC master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'sacCode',
      header: 'SAC Code',
      size: 100,
      cell: ({ getValue }) => (
        <div className="sac-code-cell text-center">
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
      cell: ({ getValue }) => (
        <div className="description-cell">
          <span className="description-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'formattedGstRate',
      header: 'GST %',
      size: 90,
      cell: ({ getValue }) => (
        <div className="rate-cell text-center">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'formattedIgstRate',
      header: 'IGST %',
      size: 90,
      cell: ({ getValue }) => (
        <div className="rate-cell text-center">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'formattedSgstRate',
      header: 'SGST %',
      size: 90,
      cell: ({ getValue }) => (
        <div className="rate-cell text-center">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'formattedCgstRate',
      header: 'CGST %',
      size: 90,
      cell: ({ getValue }) => (
        <div className="rate-cell text-center">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'formattedUtgstRate',
      header: 'UTGST %',
      size: 90,
      cell: ({ getValue }) => (
        <div className="rate-cell text-center">
          <span className="rate-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'currentVersion',
      header: 'Version',
      size: 80,
      cell: ({ getValue, row }) => {
        const sacData = row.original
        const currentRevision = sacData.currentRevision || 1
        const isClickable = currentRevision > 1
        
        return (
          <div className="version-cell text-center">
            {isClickable ? (
              <button
                className="version-link"
                onClick={() => handleRevisionHistoryClick(sacData)}
                title="Click to view revision history"
              >
                {getValue()}
              </button>
            ) : (
              <span className="version-text">{getValue()}</span>
            )}
          </div>
        )
      },
    },
  ]

  const handleBack = () => {
    navigate('/sales')
  }

  const handleAddNew = () => {
    navigate('/sales/sac-master/form')
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const handleEdit = (sacData) => {
navigate('/sales/sac-master/edit', { state: { sacData } })
  }

  const handleView = (sacData) => {
    navigate('/sales/sac-master/view', { state: { sacData } })
  }

  const handleDelete = (sacData) => {
    setDeleteItem(sacData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting SAC master record...')
      // Use _id instead of id
      const response = await sacMasterService.deleteSAC(deleteItem._id)
      if (response.success) {
        toast.success('SAC master record deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete SAC master record')
      }
    } catch (error) {
      console.error('Error deleting SAC master record:', error)
      if (error.response?.status === 404) {
        toast.error('SAC master record not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this SAC master record')
      } else {
        toast.error('Failed to delete SAC master record. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleRevisionHistoryClick = (sacData) => {
    setSelectedSACData(sacData)
    setShowRevisionHistory(true)
  }

  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales?tab=masters',
      onClick: () => navigate('/sales?tab=masters')
    },
    {
      label: 'SAC Master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={breadcrumbItems}
      />

      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1>SAC Master</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage Services Accounting Code master data</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* KPI Cards Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={20} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalSACs}</div>
          <div className="metric-title">Total SACs</div>
          <div className="metric-subtitle">All SAC codes defined</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={20} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeSACs}</div>
          <div className="metric-title">Active SACs</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <BarChart3 size={20} />
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveSACs}</div>
          <div className="metric-title">Inactive SACs</div>
          <div className="metric-subtitle">Not currently in use</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Percent size={20} />
            </div>
            <div className="metric-trend positive">
              <span>GST Rates</span>
            </div>
          </div>
          <div className="metric-value">{stats.gstRateStats?.length || 0}</div>
          <div className="metric-title">GST Rate Categories</div>
          <div className="metric-subtitle">Different GST rates configured</div>
        </div>
      </div>

      {/* Data Table Section */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          enableColumnFilters={true}
          enableColumnResizing={true}
          enableRowSelection={true}
          enablePagination={true}
          pageSize={10}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
        />
      </div>

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        revisionHistory={selectedSACData?.revisionHistory || []}
        sacCode={selectedSACData?.sacCode || ''}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete SAC Master"
        message={`Are you sure you want to delete SAC Code "${deleteItem?.sacCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default SACMaster

